import { PermissionAction } from 'api/generated/types/permissionAction';
import { PermissionEntityType } from 'api/generated/types/permissionEntityType';
import { PermissionScope } from 'api/generated/types/permissionScope';
import { UiLanguage } from 'api/generated/types/uiLanguage';
import { isSupportLoginEmail } from 'utils/supportUsers';
import { isStoredEmailDiverged } from 'utils/userInfoUtils';
import {
  isBoolean,
  isEnumValue,
  isNonEmptyString,
  isRecord,
  isString,
  parseNullableString,
} from 'utils/typeGuards';
import type { AuthMember } from 'api/generated/types/authMember';
import type { AuthOrganization } from 'api/generated/types/authOrganization';
import type { AuthPermission } from 'api/generated/types/authPermission';
import type { AuthPermissionOverride } from 'api/generated/types/authPermissionOverride';
import type { AuthRole } from 'api/generated/types/authRole';

import { asIdentityId, isAccountReady } from './sessionReadiness';

/**
 * The last verified session, stored so the next page load can render protected
 * pages while /auth/verify is still in flight. Pure: callers do the storage I/O.
 * AuthMember and AuthOrganization carry no credentials; only the tail of the
 * token is kept, to tie the cache to the token it was verified with.
 */

const CACHED_SESSION_VERSION = 1;
const TOKEN_TAIL_LENGTH = 16;

export interface SessionSnapshot {
  member: AuthMember;
  organization: AuthOrganization;
}

export interface CachedSession extends SessionSnapshot {
  v: typeof CACHED_SESSION_VERSION;
  tokenTail: string;
}

/** What the verified session means for a page rendered from the cache. */
export type SessionReconciliation = 'same' | 'update-in-place' | 'reload';

const tokenTailOf = (token: string): string => token.slice(-TOKEN_TAIL_LENGTH);

// Rebuilt field by field in a fixed order with absent optionals as null/[], so
// the stored copy drops unknown fields and two snapshots compare by value.
const normalizeRole = (role: AuthRole): AuthRole => ({
  role_id: role.role_id,
  key: role.key ?? null,
  name: role.name,
});

const normalizeMember = (member: AuthMember): AuthMember => ({
  member_id: member.member_id,
  email_address: member.email_address,
  name: member.name,
  is_active: member.is_active,
  salv_user_id: member.salv_user_id ?? null,
  role: member.role ? normalizeRole(member.role) : null,
  permissions: (member.permissions ?? []).map((permission) => ({
    action: permission.action,
    entity_type: permission.entity_type,
    scope: permission.scope,
  })),
  permission_overrides: (member.permission_overrides ?? []).map((override) => ({
    action: override.action,
    entity_type: override.entity_type,
    entity_id: override.entity_id,
    granted: override.granted,
  })),
  ui_language: member.ui_language ?? null,
  effective_ui_language: member.effective_ui_language,
});

const normalizeOrganization = (
  organization: AuthOrganization
): AuthOrganization => ({
  organization_id: organization.organization_id,
  organization_name: organization.organization_name,
  organization_slug: organization.organization_slug,
  salv_org_id: organization.salv_org_id ?? null,
  ui_language: organization.ui_language ?? null,
});

export const serializeCachedSession = (
  token: string,
  member: AuthMember,
  organization: AuthOrganization
): string => {
  const cache: CachedSession = {
    v: CACHED_SESSION_VERSION,
    tokenTail: tokenTailOf(token),
    member: normalizeMember(member),
    organization: normalizeOrganization(organization),
  };
  return JSON.stringify(cache);
};

// Each parser returns undefined for an invalid value; null is a valid value of
// the nullable fields.

const parseNullableLanguage = (
  value: unknown
): UiLanguage | null | undefined =>
  value === null || isEnumValue(UiLanguage, value) ? value : undefined;

const parseList = <T>(
  value: unknown,
  parseItem: (item: unknown) => T | undefined
): T[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const items: readonly unknown[] = value;
  const parsed: T[] = [];
  for (const item of items) {
    const result = parseItem(item);
    if (result === undefined) return undefined;
    parsed.push(result);
  }
  return parsed;
};

const parseRole = (value: unknown): AuthRole | null | undefined => {
  if (value === null) return null;
  if (!isRecord(value)) return undefined;
  const { role_id, key, name } = value;
  const parsedKey = parseNullableString(key);
  if (!isString(role_id) || !isString(name) || parsedKey === undefined) {
    return undefined;
  }
  return { role_id, key: parsedKey, name };
};

const parsePermission = (value: unknown): AuthPermission | undefined => {
  if (!isRecord(value)) return undefined;
  const { action, entity_type, scope } = value;
  if (
    !isEnumValue(PermissionAction, action) ||
    !isEnumValue(PermissionEntityType, entity_type) ||
    !isEnumValue(PermissionScope, scope)
  ) {
    return undefined;
  }
  return { action, entity_type, scope };
};

const parsePermissionOverride = (
  value: unknown
): AuthPermissionOverride | undefined => {
  if (!isRecord(value)) return undefined;
  const { action, entity_type, entity_id, granted } = value;
  if (
    !isEnumValue(PermissionAction, action) ||
    !isEnumValue(PermissionEntityType, entity_type) ||
    !isString(entity_id) ||
    !isBoolean(granted)
  ) {
    return undefined;
  }
  return { action, entity_type, entity_id, granted };
};

const parseMember = (value: unknown): AuthMember | undefined => {
  if (!isRecord(value)) return undefined;
  const { member_id, email_address, name, is_active, effective_ui_language } =
    value;
  const salvUserId = parseNullableString(value.salv_user_id);
  const role = parseRole(value.role);
  const permissions = parseList(value.permissions, parsePermission);
  const overrides = parseList(
    value.permission_overrides,
    parsePermissionOverride
  );
  const uiLanguage = parseNullableLanguage(value.ui_language);
  if (
    !isString(member_id) ||
    !isString(email_address) ||
    !isString(name) ||
    !isBoolean(is_active) ||
    !isEnumValue(UiLanguage, effective_ui_language) ||
    salvUserId === undefined ||
    role === undefined ||
    permissions === undefined ||
    overrides === undefined ||
    uiLanguage === undefined
  ) {
    return undefined;
  }
  return {
    member_id,
    email_address,
    name,
    is_active,
    salv_user_id: salvUserId,
    role,
    permissions,
    permission_overrides: overrides,
    ui_language: uiLanguage,
    effective_ui_language,
  };
};

const parseOrganization = (value: unknown): AuthOrganization | undefined => {
  if (!isRecord(value)) return undefined;
  const { organization_id, organization_name, organization_slug } = value;
  const salvOrgId = parseNullableString(value.salv_org_id);
  const uiLanguage = parseNullableLanguage(value.ui_language);
  if (
    !isString(organization_id) ||
    !isString(organization_name) ||
    !isString(organization_slug) ||
    salvOrgId === undefined ||
    uiLanguage === undefined
  ) {
    return undefined;
  }
  return {
    organization_id,
    organization_name,
    organization_slug,
    salv_org_id: salvOrgId,
    ui_language: uiLanguage,
  };
};

/** The stored cache, or null when it is missing, unreadable or another version. */
export const parseCachedSession = (
  raw: string | null
): CachedSession | null => {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // swallow-ok: Malformed cached JSON is rejected by usableCachedSession; hydration then waits for authoritative token verification instead of authenticating from the cache.
    return null;
  }
  if (!isRecord(parsed) || parsed.v !== CACHED_SESSION_VERSION) return null;
  const { tokenTail } = parsed;
  const member = parseMember(parsed.member);
  const organization = parseOrganization(parsed.organization);
  if (!isNonEmptyString(tokenTail) || !member || !organization) return null;
  return { v: CACHED_SESSION_VERSION, tokenTail, member, organization };
};

/**
 * Whether a session may be rendered before it is verified: an active customer
 * member with both Salv ids. Support sessions are never cached, since their
 * identity depends on the impersonation chosen for the load.
 */
export const sessionQualifiesForCache = ({
  member,
  organization,
  isSupportUser,
}: SessionSnapshot & { isSupportUser: boolean }): boolean =>
  !isSupportUser &&
  !isSupportLoginEmail(member.email_address) &&
  isAccountReady(member, organization);

export interface CachedSessionContext {
  cache: CachedSession | null;
  authToken: string | null;
  storedEmail: string | null;
  isSupportUser: boolean;
}

/** The cache when it belongs to the stored token and identity, else null. */
export const usableCachedSession = ({
  cache,
  authToken,
  storedEmail,
  isSupportUser,
}: CachedSessionContext): CachedSession | null => {
  if (!cache || !authToken) return null;
  if (cache.tokenTail !== tokenTailOf(authToken)) return null;
  if (!sessionQualifiesForCache({ ...cache, isSupportUser })) return null;
  // Compared exactly, like isStoredEmailDiverged: an empty mirror is not divergence.
  if (isStoredEmailDiverged(storedEmail, cache.member.email_address)) {
    return null;
  }
  return cache;
};

/**
 * How a verified session relates to the cached one already on screen. A
 * different org, user or email means the page rendered another identity's
 * data, so it reloads; any other difference (role, permissions, names,
 * languages) is applied in place.
 */
export const reconcileVerifiedSession = (
  cached: SessionSnapshot,
  verified: SessionSnapshot
): SessionReconciliation => {
  if (
    asIdentityId(cached.organization.salv_org_id) !==
      asIdentityId(verified.organization.salv_org_id) ||
    asIdentityId(cached.member.salv_user_id) !==
      asIdentityId(verified.member.salv_user_id) ||
    cached.member.email_address !== verified.member.email_address
  ) {
    return 'reload';
  }
  const canonical = (snapshot: SessionSnapshot): string =>
    JSON.stringify([
      normalizeMember(snapshot.member),
      normalizeOrganization(snapshot.organization),
    ]);
  return canonical(cached) === canonical(verified) ? 'same' : 'update-in-place';
};
